import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const AdminTrainingManagement = ({ onNavigate, userInfo = {} }) => {
    // Mobile state (used for responsive content layout)
    const [isMobile, setIsMobile] = useState(false);

    // State for training enrollments data
    const [allEnrollments, setAllEnrollments] = useState([]);
    const [filteredEnrollments, setFilteredEnrollments] = useState([]);
    const [displayedEnrollments, setDisplayedEnrollments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [initialLoad, setInitialLoad] = useState(true);

    // Pagination and search state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [limit] = useState(10);

    // Filtering states
    const [filterDepartment, setFilterDepartment] = useState('');
    const [filterYear, setFilterYear] = useState('');
    const [filterBatch, setFilterBatch] = useState('');
    const [filterProgram, setFilterProgram] = useState('');
    const [filterStatus, setFilterStatus] = useState('');

    // Modal states
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showBulkEnrollModal, setShowBulkEnrollModal] = useState(false);
    const [selectedEnrollment, setSelectedEnrollment] = useState(null);

    // Training data states
    const [trainingPrograms, setTrainingPrograms] = useState([]);
    const [trainingBatches, setTrainingBatches] = useState([]);
    const [availableStudents, setAvailableStudents] = useState([]);
    const [selectedStudents, setSelectedStudents] = useState([]);

    // Department and batch data
    const [departments, setDepartments] = useState([]);
    const [batches, setBatches] = useState([]);
    const [filterBatches, setFilterBatches] = useState([]);

    // Loading states
    const [departmentsLoading, setDepartmentsLoading] = useState(false);
    const [batchesLoading, setBatchesLoading] = useState(false);
    const [filterBatchesLoading, setFilterBatchesLoading] = useState(false);
    const [programsLoading, setProgramsLoading] = useState(false);
    const [trainingBatchesLoading, setTrainingBatchesLoading] = useState(false);
    const [studentsLoading, setStudentsLoading] = useState(false);

    // Form state for enrollment
    const [formData, setFormData] = useState({
        student_id: '',
        training_program_id: '',
        training_batch_id: '',
        enrollment_date: new Date().toISOString().split('T')[0],
        status: 'enrolled',
        remarks: ''
    });

    // Bulk enrollment state
    const [bulkFormData, setBulkFormData] = useState({
        training_program_id: '',
        training_batch_id: '',
        enrollment_date: new Date().toISOString().split('T')[0],
        student_filter_dept: '',
        student_filter_year: '',
        student_filter_batch: ''
    });

    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    // Year options
    const yearOptions = [
        { value: 1, label: '1st Year' },
        { value: 2, label: '2nd Year' },
        { value: 3, label: '3rd Year' },
        { value: 4, label: '4th Year' }
    ];

    // Status options
    const statusOptions = [
        { value: 'enrolled', label: 'Enrolled' },
        { value: 'completed', label: 'Completed' },
        { value: 'dropped', label: 'Dropped' },
        { value: 'suspended', label: 'Suspended' }
    ];

    // Check if mobile on mount and resize
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 1024);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // ENHANCED API HELPER FUNCTION WITH BETTER ERROR HANDLING
    const apiCall = async (endpoint, options = {}) => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        if (!token) {
            throw new Error('Authentication token not found');
        }

        try {
            const response = await fetch(`${API_LINK}/api/admin${endpoint}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    ...options.headers
                },
                ...options
            });

            // Get response text first to handle both JSON and non-JSON responses
            const responseText = await response.text();

            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem('token');
                    sessionStorage.removeItem('token');
                    throw new Error('Session expired');
                }

                // Try to parse error message from response
                let errorMessage = `HTTP error! status: ${response.status}`;
                try {
                    const errorData = JSON.parse(responseText);
                    errorMessage = errorData.message || errorData.error || errorMessage;
                } catch (e) {
                    // If response is not JSON, use the text as error message
                    if (responseText) {
                        errorMessage = responseText;
                    }
                }

                const error = new Error(errorMessage);
                error.status = response.status;
                error.response = response;
                throw error;
            }

            // Try to parse as JSON, fallback to text
            try {
                return JSON.parse(responseText);
            } catch (e) {
                return { success: true, data: responseText };
            }
        } catch (error) {
            console.error('API call failed:', {
                endpoint,
                error: error.message,
                status: error.status
            });
            throw error;
        }
    };

    // FETCH DEPARTMENTS
    const fetchDepartments = async () => {
        try {
            setDepartmentsLoading(true);
            const result = await apiCall('/departments');

            if (result.success) {
                setDepartments(result.data);
            }
        } catch (error) {
            console.error('Error fetching departments:', error);
            setError('Failed to fetch departments: ' + error.message);
        } finally {
            setDepartmentsLoading(false);
        }
    };

    // FETCH BATCHES FOR STUDENT FILTERING
    const fetchFilterBatches = async (department, year) => {
        if (!department || !year) {
            setFilterBatches([]);
            return;
        }

        try {
            setFilterBatchesLoading(true);
            const result = await apiCall(`/batches?department=${encodeURIComponent(department)}&year=${year}`);

            if (result.success && result.data) {
                setFilterBatches(result.data);
            } else {
                setFilterBatches([]);
            }
        } catch (error) {
            console.error('Error fetching filter batches:', error);
            setFilterBatches([]);
        } finally {
            setFilterBatchesLoading(false);
        }
    };

    // FETCH TRAINING PROGRAMS
    const fetchTrainingPrograms = async () => {
        try {
            setProgramsLoading(true);
            const result = await apiCall('/training-programs?status=active');

            if (result.success) {
                setTrainingPrograms(result.data.programs);
            }
        } catch (error) {
            console.error('Error fetching training programs:', error);
            setError('Failed to fetch training programs: ' + error.message);
        } finally {
            setProgramsLoading(false);
        }
    };

    // FETCH TRAINING BATCHES
    const fetchTrainingBatches = async (programId) => {
        if (!programId) {
            setTrainingBatches([]);
            return;
        }

        try {
            console.log('🚀 Fetching batches for program ID:', programId);
            setTrainingBatchesLoading(true);

            const result = await apiCall(`/training-batches?program_id=${programId}&status=active`);

            console.log('📦 API Response:', result);

            if (result.success && result.data && result.data.batches) {
                console.log('✅ Found batches:', result.data.batches.length);
                setTrainingBatches(result.data.batches);
            } else {
                console.log('❌ No batches found or API error');
                setTrainingBatches([]);
            }
        } catch (error) {
            console.error('💥 Error fetching training batches:', error);
            setTrainingBatches([]);
        } finally {
            setTrainingBatchesLoading(false);
        }
    };

    // FETCH AVAILABLE STUDENTS
    const fetchAvailableStudents = async (department, year, batch) => {
        if (!department) {
            setAvailableStudents([]);
            return;
        }

        try {
            setStudentsLoading(true);
            const params = new URLSearchParams();
            params.append('department', department);
            if (year) params.append('year', year);
            if (batch) params.append('batch', batch);

            const result = await apiCall(`/training-students/available?${params.toString()}`);

            if (result.success) {
                setAvailableStudents(result.data.students);
            } else {
                setAvailableStudents([]);
            }
        } catch (error) {
            console.error('Error fetching available students:', error);
            setAvailableStudents([]);
        } finally {
            setStudentsLoading(false);
        }
    };

    // FETCH ALL TRAINING ENROLLMENTS
    const fetchAllEnrollments = async () => {
        try {
            setLoading(true);
            setError(null);

            const result = await apiCall('/training-enrollments?page=1&limit=10000');

            if (result.success) {
                const enrollmentsData = result.data?.enrollments || [];
                setAllEnrollments(enrollmentsData);

                if (initialLoad) {
                    setFilteredEnrollments([]);
                    setDisplayedEnrollments([]);
                    setTotalCount(0);
                    setTotalPages(0);
                    setCurrentPage(1);
                    setInitialLoad(false);
                } else {
                    applyFilters(enrollmentsData, searchTerm, filterDepartment, filterYear, filterBatch, filterProgram, filterStatus);
                }
            } else {
                setError(result.message || 'Failed to fetch training enrollments');
            }
        } catch (error) {
            console.error('Fetch enrollments error:', error);
            setError(`Failed to load training enrollments: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Client-side filtering function
    const applyFilters = (enrollments, searchText, department, year, batch, program, status) => {
        let filtered = [...enrollments];

        // Apply search filter
        if (searchText && searchText.trim()) {
            const searchLower = searchText.trim().toLowerCase();
            filtered = filtered.filter(enrollment =>
                enrollment.student_id?.toLowerCase().includes(searchLower) ||
                enrollment.student_name?.toLowerCase().includes(searchLower) ||
                enrollment.actual_student_name?.toLowerCase().includes(searchLower) ||
                enrollment.program_name?.toLowerCase().includes(searchLower) ||
                enrollment.batch_name?.toLowerCase().includes(searchLower)
            );
        }

        // Apply department filter (using student's department)
        if (department && department.trim()) {
            filtered = filtered.filter(enrollment =>
                enrollment.dept === department
            );
        }

        // Apply year filter
        if (year) {
            const dbYear = 2000 + parseInt(year);
            filtered = filtered.filter(enrollment =>
                enrollment.year === dbYear
            );
        }

        // Apply batch filter (student's batch)
        if (batch && batch.trim()) {
            filtered = filtered.filter(enrollment =>
                enrollment.student_batch === batch
            );
        }

        // Apply program filter
        if (program && program.trim()) {
            filtered = filtered.filter(enrollment =>
                enrollment.training_program_id == program
            );
        }

        // Apply status filter
        if (status && status.trim()) {
            filtered = filtered.filter(enrollment =>
                enrollment.status === status
            );
        }

        setFilteredEnrollments(filtered);
        setTotalCount(filtered.length);
        setTotalPages(Math.ceil(filtered.length / limit));
        setCurrentPage(1);

        applyPagination(filtered, 1);
    };

    // Client-side pagination function
    const applyPagination = (filteredData, page) => {
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedData = filteredData.slice(startIndex, endIndex);

        setDisplayedEnrollments(paginatedData);
        setCurrentPage(page);
    };

    // INITIAL DATA FETCH
    useEffect(() => {
        fetchDepartments();
        fetchTrainingPrograms();
        fetchAllEnrollments();
    }, []);

    // Filter handlers
    const handleFilterDepartmentChange = (value) => {
        setFilterDepartment(value);
        setFilterYear('');
        setFilterBatch('');
        setFilterBatches([]);
    };

    const handleFilterYearChange = (value) => {
        const yearValue = value ? parseInt(value) : '';
        setFilterYear(yearValue);
        setFilterBatch('');

        if (yearValue && filterDepartment) {
            const dbYear = 2000 + yearValue;
            fetchFilterBatches(filterDepartment, dbYear);
        } else {
            setFilterBatches([]);
        }
    };

    const handleClearFilters = () => {
        setFilterDepartment('');
        setFilterYear('');
        setFilterBatch('');
        setFilterProgram('');
        setFilterStatus('');
        setFilterBatches([]);
        setSearchTerm('');

        setFilteredEnrollments([]);
        setDisplayedEnrollments([]);
        setTotalCount(0);
        setTotalPages(0);
        setCurrentPage(1);
    };

    // Handle filter changes - instant filtering
    useEffect(() => {
        if (allEnrollments.length > 0) {
            const hasActiveFilters = searchTerm.trim() || filterDepartment || filterYear || filterBatch || filterProgram || filterStatus;

            if (hasActiveFilters) {
                applyFilters(allEnrollments, searchTerm, filterDepartment, filterYear, filterBatch, filterProgram, filterStatus);
            } else {
                setFilteredEnrollments([]);
                setDisplayedEnrollments([]);
                setTotalCount(0);
                setTotalPages(0);
                setCurrentPage(1);
            }
        }
    }, [searchTerm, filterDepartment, filterYear, filterBatch, filterProgram, filterStatus, allEnrollments]);

    // Handle pagination changes
    const handlePageChange = (page) => {
        if (page && page >= 1 && page <= totalPages) {
            applyPagination(filteredEnrollments, page);
        }
    };

    // Form handlers
    const resetForm = () => {
        setFormData({
            student_id: '',
            training_program_id: '',
            training_batch_id: '',
            enrollment_date: new Date().toISOString().split('T')[0],
            status: 'enrolled',
            remarks: ''
        });
        setFormErrors({});
        setSelectedEnrollment(null);
        setTrainingBatches([]);
    };

    const resetBulkForm = () => {
        setBulkFormData({
            training_program_id: '',
            training_batch_id: '',
            enrollment_date: new Date().toISOString().split('T')[0],
            student_filter_dept: '',
            student_filter_year: '',
            student_filter_batch: ''
        });
        setSelectedStudents([]);
        setAvailableStudents([]);
        setTrainingBatches([]);
        setBatches([]);
    };

    // ENHANCED FORM VALIDATION
    const validateForm = () => {
        const errors = {};

        // Student ID validation (supports your format with hyphens)
        if (!formData.student_id || !formData.student_id.trim()) {
            errors.student_id = 'Student ID is required';
        } else if (formData.student_id.trim().length < 3) {
            errors.student_id = 'Student ID must be at least 3 characters';
        } else if (!/^[A-Za-z0-9\-]+$/.test(formData.student_id.trim())) {
            errors.student_id = 'Student ID can only contain letters, numbers, and hyphens';
        }

        // Program validation
        if (!formData.training_program_id) {
            errors.training_program_id = 'Training program is required';
        }

        // Batch validation
        if (!formData.training_batch_id) {
            errors.training_batch_id = 'Training batch is required';
        }

        // Date validation
        if (!formData.enrollment_date) {
            errors.enrollment_date = 'Enrollment date is required';
        } else {
            const enrollmentDate = new Date(formData.enrollment_date);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (enrollmentDate > today) {
                errors.enrollment_date = 'Enrollment date cannot be in the future';
            }
        }

        // Status validation
        if (!formData.status) {
            errors.status = 'Status is required';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // ENHANCED FORM SUBMISSION WITH PROPER DATA TYPE CONVERSION
    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setSubmitting(true);
            setFormErrors({}); // Clear previous errors

            // CRITICAL FIX: Convert IDs to integers and properly format data
            const submissionData = {
                student_id: formData.student_id.trim(),
                training_program_id: parseInt(formData.training_program_id, 10),
                training_batch_id: parseInt(formData.training_batch_id, 10),
                enrollment_date: formData.enrollment_date,
                status: formData.status,
                remarks: formData.remarks || ''
            };

            // Validate the converted IDs
            if (isNaN(submissionData.training_program_id) || isNaN(submissionData.training_batch_id)) {
                throw new Error('Invalid program or batch selection');
            }

            console.log('Submitting enrollment data:', submissionData); // Debug log

            if (selectedEnrollment) {
                // Update enrollment
                const result = await apiCall(`/training-enrollments/${selectedEnrollment.id}`, {
                    method: 'PUT',
                    body: JSON.stringify(submissionData)
                });
                alert('Training enrollment updated successfully!');
            } else {
                // Create enrollment
                const result = await apiCall('/training-enrollments', {
                    method: 'POST',
                    body: JSON.stringify(submissionData)
                });

                if (result.success) {
                    alert('Student enrolled in training program successfully!');
                } else {
                    throw new Error(result.message || 'Failed to create enrollment');
                }
            }

            setShowAddModal(false);
            setShowEditModal(false);
            resetForm();
            setInitialLoad(false);
            await fetchAllEnrollments();

        } catch (error) {
            console.error('Submit error details:', error);

            // Handle specific error cases
            if (error.message.includes('400') || error.status === 400) {
                if (error.message.includes('student') || error.message.includes('Student')) {
                    setFormErrors({ student_id: 'Student ID not found or invalid' });
                    alert('Error: Student ID not found. Please verify the student ID is correct.');
                } else if (error.message.includes('duplicate') || error.message.includes('already enrolled')) {
                    alert('Error: Student is already enrolled in this training program.');
                } else if (error.message.includes('batch') || error.message.includes('program')) {
                    setFormErrors({
                        training_batch_id: 'Selected batch may not belong to the chosen program'
                    });
                    alert('Error: Invalid training batch selection for this program.');
                } else {
                    alert(`Error: ${error.message}`);
                }
            } else {
                alert('Failed to save enrollment. Please check your connection and try again.');
            }

            console.error('Full error:', error);
        } finally {
            setSubmitting(false);
        }
    };

    // ENHANCED BULK ENROLLMENT WITH PROPER DATA TYPE CONVERSION
    const handleBulkEnroll = async () => {
        if (selectedStudents.length === 0) {
            alert('Please select at least one student');
            return;
        }

        if (!bulkFormData.training_program_id || !bulkFormData.training_batch_id) {
            alert('Please select training program and batch');
            return;
        }

        try {
            setSubmitting(true);

            // CRITICAL FIX: Convert IDs to integers for bulk enrollment
            const bulkSubmissionData = {
                student_ids: selectedStudents,
                training_program_id: parseInt(bulkFormData.training_program_id, 10),
                training_batch_id: parseInt(bulkFormData.training_batch_id, 10),
                enrollment_date: bulkFormData.enrollment_date
            };

            // Validate the converted IDs
            if (isNaN(bulkSubmissionData.training_program_id) || isNaN(bulkSubmissionData.training_batch_id)) {
                throw new Error('Invalid program or batch selection');
            }

            console.log('Submitting bulk enrollment data:', bulkSubmissionData); // Debug log

            const result = await apiCall('/training-enrollments/bulk-enroll', {
                method: 'POST',
                body: JSON.stringify(bulkSubmissionData)
            });

            if (result.success) {
                alert(`Bulk enrollment completed! ${result.data.total_successful} students enrolled successfully.`);
                setShowBulkEnrollModal(false);
                resetBulkForm();
                setInitialLoad(false);
                await fetchAllEnrollments();
            }

        } catch (error) {
            console.error('Bulk enroll error:', error);
            if (error.status === 400) {
                alert(`Failed to bulk enroll students: ${error.message}`);
            } else {
                alert('Failed to bulk enroll students. Please try again.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    // Handle delete
    const handleDelete = async () => {
        if (!selectedEnrollment) return;

        try {
            setSubmitting(true);

            await apiCall(`/training-enrollments/${selectedEnrollment.id}`, {
                method: 'DELETE'
            });

            setShowDeleteModal(false);
            setSelectedEnrollment(null);
            setInitialLoad(false);
            await fetchAllEnrollments();

            alert('Training enrollment deleted successfully!');
        } catch (error) {
            alert('Failed to delete enrollment. Please try again.');
            console.error('Delete error:', error);
        } finally {
            setSubmitting(false);
        }
    };

    // Handle edit click
    const handleEditClick = (enrollment) => {
        setSelectedEnrollment(enrollment);
        setFormData({
            student_id: enrollment.student_id || '',
            training_program_id: enrollment.training_program_id || '',
            training_batch_id: enrollment.training_batch_id || '',
            enrollment_date: enrollment.enrollment_date || new Date().toISOString().split('T')[0],
            status: enrollment.status || 'enrolled',
            remarks: enrollment.remarks || ''
        });

        if (enrollment.training_program_id) {
            fetchTrainingBatches(enrollment.training_program_id);
        }

        setShowEditModal(true);
    };

    // Handle delete click
    const handleDeleteClick = (enrollment) => {
        setSelectedEnrollment(enrollment);
        setShowDeleteModal(true);
    };



    // Get year display
    const getYearDisplay = (year) => {
        const yearMap = {
            2001: '1st Year',
            2002: '2nd Year',
            2003: '3rd Year',
            2004: '4th Year'
        };
        return yearMap[year] || `Year ${year}`;
    };

    // Get status badge color
    const getStatusColor = (status) => {
        const colors = {
            enrolled: { bg: '#e8f5e9', color: '#2e7d32' },
            completed: { bg: '#e3f2fd', color: '#1976d2' },
            dropped: { bg: '#ffebee', color: '#d32f2f' },
            suspended: { bg: '#fff3e0', color: '#f57c00' }
        };
        return colors[status] || { bg: '#f5f5f5', color: '#666' };
    };

    // Check if any filters are active
    const hasActiveFilters = searchTerm.trim() || filterDepartment || filterYear || filterBatch || filterProgram || filterStatus;

    return (
        <Layout role="admin" onNavigate={onNavigate} userInfo={userInfo} activePage="admin-training">
            <style>
                {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
            </style>
            {/* Welcome Banner */}
            <div style={{
                background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                color: '#ffffff',
                padding: '2rem',
                borderRadius: '12px',
                marginBottom: '2rem',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}>
                <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.5rem' }}>
                    Training Management Portal 🎓
                </h2>
                <p style={{ margin: 0, opacity: 0.9 }}>
                    Manage training enrollments, assign students to training programs, and track progress.
                </p>
            </div>

            {/* FILTERS SECTION */}
            <div style={{
                background: '#ffffff',
                padding: '2rem',
                borderRadius: '12px',
                marginBottom: '2rem',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    marginBottom: '1.5rem'
                }}>
                    <span style={{ fontSize: '1.5rem' }}>🔍</span>
                    <h3 style={{ margin: 0, fontSize: '1.3rem', color: '#333' }}>
                        Filter Training Enrollments
                    </h3>
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '1rem',
                    marginBottom: '1rem'
                }}>
                    {/* Department Filter */}
                    <div>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.5rem',
                            fontWeight: '600',
                            color: '#374151',
                            fontSize: '0.9rem'
                        }}>
                            Student Department
                        </label>
                        <select
                            value={filterDepartment}
                            onChange={(e) => handleFilterDepartmentChange(e.target.value)}
                            disabled={departmentsLoading}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '2px solid #e5e7eb',
                                borderRadius: '8px',
                                fontSize: '0.9rem',
                                outline: 'none',
                                transition: 'border-color 0.3s ease',
                                backgroundColor: departmentsLoading ? '#f9fafb' : '#ffffff',
                                cursor: departmentsLoading ? 'not-allowed' : 'pointer'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#238548'}
                            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                        >
                            <option value="">
                                {departmentsLoading ? 'Loading...' : 'All Departments'}
                            </option>
                            {departments.map(dept => (
                                <option key={dept.value} value={dept.value}>
                                    {dept.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Year Filter */}
                    <div>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.5rem',
                            fontWeight: '600',
                            color: '#374151',
                            fontSize: '0.9rem'
                        }}>
                            Student Year
                        </label>
                        <select
                            value={filterYear}
                            onChange={(e) => handleFilterYearChange(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '2px solid #e5e7eb',
                                borderRadius: '8px',
                                fontSize: '0.9rem',
                                outline: 'none',
                                transition: 'border-color 0.3s ease',
                                backgroundColor: '#ffffff',
                                cursor: 'pointer'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#238548'}
                            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                        >
                            <option value="">All Years</option>
                            {yearOptions.map(year => (
                                <option key={year.value} value={year.value}>
                                    {year.label}
                                </option>
                            ))}
                        </select>
                    </div>


                    {/* Training Program Filter */}
                    <div>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.5rem',
                            fontWeight: '600',
                            color: '#374151',
                            fontSize: '0.9rem'
                        }}>
                            Training Program
                        </label>
                        <select
                            value={filterProgram}
                            onChange={(e) => setFilterProgram(e.target.value)}
                            disabled={programsLoading}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '2px solid #e5e7eb',
                                borderRadius: '8px',
                                fontSize: '0.9rem',
                                outline: 'none',
                                transition: 'border-color 0.3s ease',
                                backgroundColor: programsLoading ? '#f9fafb' : '#ffffff',
                                cursor: programsLoading ? 'not-allowed' : 'pointer'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#238548'}
                            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                        >
                            <option value="">
                                {programsLoading ? 'Loading...' : 'All Programs'}
                            </option>
                            {trainingPrograms.map(program => (
                                <option key={program.id} value={program.id}>
                                    {program.program_name} ({program.program_code})
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Status Filter */}
                    <div>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.5rem',
                            fontWeight: '600',
                            color: '#374151',
                            fontSize: '0.9rem'
                        }}>
                            Status
                        </label>
                        <select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.75rem',
                                border: '2px solid #e5e7eb',
                                borderRadius: '8px',
                                fontSize: '0.9rem',
                                outline: 'none',
                                transition: 'border-color 0.3s ease',
                                backgroundColor: '#ffffff',
                                cursor: 'pointer'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#238548'}
                            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                        >
                            <option value="">All Statuses</option>
                            {statusOptions.map(status => (
                                <option key={status.value} value={status.value}>
                                    {status.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>


                {/* Clear Filters Button */}
                <div style={{
                    display: 'flex',
                    gap: '1rem',
                    marginBottom: '1rem'
                }}>
                    <button
                        onClick={handleClearFilters}
                        style={{
                            padding: '0.75rem 1.5rem',
                            background: 'transparent',
                            color: '#6b7280',
                            border: '2px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.borderColor = '#d1d5db';
                            e.target.style.backgroundColor = '#f9fafb';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.borderColor = '#e5e7eb';
                            e.target.style.backgroundColor = 'transparent';
                        }}
                    >
                        <span>🗑️</span>
                        Clear All Filters
                    </button>
                </div>

                {/* Active Filters Display */}
                {hasActiveFilters && (
                    <div style={{
                        padding: '1rem',
                        backgroundColor: '#f0f9ff',
                        borderRadius: '8px',
                        border: '1px solid #bae6fd'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            marginBottom: '0.5rem',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            color: '#0369a1'
                        }}>
                            <span>📌</span>
                            Active Filters:
                        </div>
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '0.5rem'
                        }}>
                            {searchTerm.trim() && (
                                <span style={{
                                    padding: '0.25rem 0.75rem',
                                    backgroundColor: '#7c3aed',
                                    color: '#ffffff',
                                    borderRadius: '20px',
                                    fontSize: '0.8rem',
                                    fontWeight: '500'
                                }}>
                                    Search: {searchTerm.trim()}
                                </span>
                            )}
                            {filterDepartment && (
                                <span style={{
                                    padding: '0.25rem 0.75rem',
                                    backgroundColor: '#1e40af',
                                    color: '#ffffff',
                                    borderRadius: '20px',
                                    fontSize: '0.8rem',
                                    fontWeight: '500'
                                }}>
                                    Dept: {departments.find(d => d.value === filterDepartment)?.label || filterDepartment}
                                </span>
                            )}
                            {filterYear && (
                                <span style={{
                                    padding: '0.25rem 0.75rem',
                                    backgroundColor: '#dc2626',
                                    color: '#ffffff',
                                    borderRadius: '20px',
                                    fontSize: '0.8rem',
                                    fontWeight: '500'
                                }}>
                                    Year: {yearOptions.find(y => y.value == filterYear)?.label || filterYear}
                                </span>
                            )}
                            {filterBatch && (
                                <span style={{
                                    padding: '0.25rem 0.75rem',
                                    backgroundColor: '#059669',
                                    color: '#ffffff',
                                    borderRadius: '20px',
                                    fontSize: '0.8rem',
                                    fontWeight: '500'
                                }}>
                                    Batch: {filterBatch}
                                </span>
                            )}
                            {filterProgram && (
                                <span style={{
                                    padding: '0.25rem 0.75rem',
                                    backgroundColor: '#7c2d12',
                                    color: '#ffffff',
                                    borderRadius: '20px',
                                    fontSize: '0.8rem',
                                    fontWeight: '500'
                                }}>
                                    Program: {trainingPrograms.find(p => p.id == filterProgram)?.program_name || filterProgram}
                                </span>
                            )}
                            {filterStatus && (
                                <span style={{
                                    padding: '0.25rem 0.75rem',
                                    backgroundColor: '#9333ea',
                                    color: '#ffffff',
                                    borderRadius: '20px',
                                    fontSize: '0.8rem',
                                    fontWeight: '500'
                                }}>
                                    Status: {statusOptions.find(s => s.value === filterStatus)?.label || filterStatus}
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* No Filter Warning */}
                {!hasActiveFilters && !loading && (
                    <div style={{
                        padding: '1rem',
                        backgroundColor: '#fef3c7',
                        borderRadius: '8px',
                        border: '1px solid #f59e0b',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                    }}>
                        <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                        <p style={{ margin: 0, color: '#92400e', fontSize: '0.9rem' }}>
                            Use filters above to view and manage training enrollments.
                        </p>
                    </div>
                )}
            </div>

            {/* Search and Controls Section */}
            <div style={{
                background: '#ffffff',
                padding: '2rem',
                borderRadius: '12px',
                marginBottom: '2rem',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
            }}>
                <div style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'stretch' : 'center',
                    gap: '1rem',
                    marginBottom: '1rem'
                }}>
                    {/* Search Bar */}
                    <div style={{ flex: 1, maxWidth: isMobile ? '100%' : '400px' }}>
                        <div style={{ position: 'relative' }}>
                            <input
                                type="text"
                                placeholder="Search by student name, ID, or program..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '1rem 3rem 1rem 1rem',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '8px',
                                    fontSize: '1rem',
                                    outline: 'none',
                                    transition: 'all 0.3s ease'
                                }}
                                onFocus={(e) => {
                                    e.target.style.borderColor = '#238548';
                                    e.target.style.boxShadow = '0 0 0 3px rgba(35, 133, 72, 0.1)';
                                }}
                                onBlur={(e) => {
                                    e.target.style.borderColor = '#e2e8f0';
                                    e.target.style.boxShadow = 'none';
                                }}
                            />
                            <button
                                style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '1.2rem',
                                    cursor: 'pointer',
                                    color: '#666',
                                    padding: '0.5rem'
                                }}
                            >
                                🔍
                            </button>
                        </div>
                    </div>

                    {/* Add Enrollment Button */}
                    <button
                        onClick={() => {
                            resetForm();
                            setShowAddModal(true);
                        }}
                        style={{
                            padding: '1rem 2rem',
                            background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 8px 25px rgba(35, 133, 72, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'none';
                        }}
                    >
                        <span style={{ fontSize: '1.2rem' }}>+</span>
                        Enroll Student
                    </button>

                    {/* Bulk Enroll Button */}
                    <button
                        onClick={() => {
                            resetBulkForm();
                            setShowBulkEnrollModal(true);
                        }}
                        style={{
                            padding: '1rem 2rem',
                            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            fontWeight: '500',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 8px 25px rgba(25, 118, 210, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = 'none';
                        }}
                    >
                        <span style={{ fontSize: '1.2rem' }}>👥</span>
                        Bulk Enroll
                    </button>

                    {/* Refresh Button */}
                    <button
                        onClick={() => {
                            setInitialLoad(false);
                            fetchAllEnrollments();
                        }}
                        disabled={loading}
                        style={{
                            padding: '1rem',
                            background: '#f0f9ff',
                            color: '#0369a1',
                            border: '2px solid #e0f2fe',
                            borderRadius: '8px',
                            fontSize: '1rem',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: loading ? 0.6 : 1
                        }}
                        onMouseEnter={(e) => {
                            if (!loading) {
                                e.target.style.backgroundColor = '#0369a1';
                                e.target.style.color = '#ffffff';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!loading) {
                                e.target.style.backgroundColor = '#f0f9ff';
                                e.target.style.color = '#0369a1';
                            }
                        }}
                        title="Refresh enrollments"
                    >
                        {loading ? (
                            <div style={{
                                width: '16px',
                                height: '16px',
                                border: '2px solid #0369a1',
                                borderTop: '2px solid transparent',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                            }}></div>
                        ) : (
                            '🔄'
                        )}
                    </button>
                </div>

                {/* Stats */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: '1rem',
                    borderTop: '1px solid #ecf0f1',
                    fontSize: '0.9rem',
                    color: '#666'
                }}>
                    <span>
                        {loading && allEnrollments.length === 0 ? 'Loading enrollments...' :
                            error ? 'Error loading enrollments' :
                                hasActiveFilters ? `Showing ${displayedEnrollments.length} of ${totalCount} enrollments (filtered from ${allEnrollments.length} total)` :
                                    `${allEnrollments.length} enrollments loaded - Use filters to view data`}
                    </span>
                    {loading && (
                        <span style={{ color: '#238548' }}>
                            <div style={{
                                width: '16px',
                                height: '16px',
                                border: '2px solid #e3f2fd',
                                borderTop: '2px solid #238548',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite',
                                display: 'inline-block'
                            }}></div>
                        </span>
                    )}
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div style={{
                    background: '#fee2e2',
                    border: '1px solid #fecaca',
                    color: '#dc2626',
                    padding: '1rem',
                    borderRadius: '12px',
                    marginBottom: '2rem'
                }}>
                    <p style={{ margin: '0 0 0.5rem 0', fontWeight: '500' }}>⚠️ Error Loading Enrollments</p>
                    <p style={{ margin: '0 0 1rem 0' }}>{error}</p>
                    <button
                        onClick={() => {
                            setInitialLoad(false);
                            fetchAllEnrollments();
                        }}
                        style={{
                            background: '#dc2626',
                            color: 'white',
                            border: 'none',
                            padding: '0.5rem 1rem',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.9rem'
                        }}
                    >
                        Try Again
                    </button>
                </div>
            )}

            {/* Training Enrollments Table */}
            <div style={{
                background: '#ffffff',
                borderRadius: '12px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                overflow: 'hidden'
            }}>
                {loading && allEnrollments.length === 0 ? (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '4rem',
                        flexDirection: 'column',
                        gap: '1rem'
                    }}>
                        <div style={{
                            width: '50px',
                            height: '50px',
                            border: '4px solid #e3f2fd',
                            borderTop: '4px solid #238548',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                        }}></div>
                        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading training enrollments...</p>
                    </div>
                ) : displayedEnrollments.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '4rem 2rem'
                    }}>
                        <div style={{ fontSize: '4rem', marginBottom: '1rem', opacity: 0.5 }}>🎓</div>
                        <h3 style={{
                            fontSize: '1.5rem',
                            fontWeight: 600,
                            color: '#333',
                            margin: '0 0 1rem 0'
                        }}>
                            {hasActiveFilters ? 'No training enrollments found' : 'Ready to manage training enrollments'}
                        </h3>
                        <p style={{ color: '#666', margin: '0 0 2rem 0', fontSize: '1rem' }}>
                            {hasActiveFilters
                                ? 'Try adjusting your search criteria or filters'
                                : 'Apply filters above to view training enrollments or start by enrolling students'
                            }
                        </p>
                        {!hasActiveFilters && (
                            <div style={{
                                padding: '1rem',
                                backgroundColor: '#e0f2fe',
                                borderRadius: '8px',
                                margin: '0 auto',
                                maxWidth: '500px'
                            }}>
                                <p style={{ margin: 0, color: '#0369a1', fontSize: '0.9rem' }}>
                                    <strong>📋 How to manage training enrollments:</strong><br />
                                    1. Use "Enroll Student" to add individual enrollments<br />
                                    2. Use "Bulk Enroll" to add multiple students at once<br />
                                    3. Apply filters to view existing enrollments<br />
                                    4. Use search to find specific students or programs
                                </p>
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                fontSize: isMobile ? '0.85rem' : '0.95rem'
                            }}>
                                <thead>
                                    <tr style={{
                                        background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                                        color: '#ffffff'
                                    }}>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            fontWeight: 600,
                                            borderBottom: 'none',
                                            whiteSpace: 'nowrap'
                                        }}>Student</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            fontWeight: 600,
                                            borderBottom: 'none',
                                            whiteSpace: 'nowrap'
                                        }}>Training Program</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            fontWeight: 600,
                                            borderBottom: 'none',
                                            whiteSpace: 'nowrap'
                                        }}>Batch</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            fontWeight: 600,
                                            borderBottom: 'none',
                                            whiteSpace: 'nowrap'
                                        }}>Department</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            fontWeight: 600,
                                            borderBottom: 'none',
                                            whiteSpace: 'nowrap'
                                        }}>Year</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            fontWeight: 600,
                                            borderBottom: 'none',
                                            whiteSpace: 'nowrap'
                                        }}>Status</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'left',
                                            fontWeight: 600,
                                            borderBottom: 'none',
                                            whiteSpace: 'nowrap'
                                        }}>Enrolled Date</th>
                                        <th style={{
                                            padding: '1rem',
                                            textAlign: 'center',
                                            fontWeight: 600,
                                            borderBottom: 'none',
                                            whiteSpace: 'nowrap'
                                        }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayedEnrollments.map((enrollment, index) => (
                                        <tr key={enrollment.id} style={{
                                            borderBottom: '1px solid #f1f5f9',
                                            transition: 'background-color 0.3s ease'
                                        }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                        >
                                            <td style={{ padding: '1rem' }}>
                                                <div>
                                                    <div style={{ fontWeight: 500, color: '#333', marginBottom: '0.25rem' }}>
                                                        {enrollment.actual_student_name || enrollment.student_name}
                                                    </div>
                                                    <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                                        ID: {enrollment.student_id}
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{ padding: '1rem', color: '#333' }}>
                                                <div>
                                                    <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>
                                                        {enrollment.program_name}
                                                    </div>
                                                    <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                                        {enrollment.program_code}
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{ padding: '1rem', color: '#333' }}>
                                                <span style={{
                                                    padding: '0.25rem 0.75rem',
                                                    backgroundColor: '#e3f2fd',
                                                    color: '#1976d2',
                                                    borderRadius: '20px',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 500
                                                }}>
                                                    {enrollment.batch_name}
                                                </span>
                                            </td>
                                            <td style={{ padding: '1rem', color: '#333' }}>
                                                <span style={{
                                                    padding: '0.25rem 0.75rem',
                                                    backgroundColor: '#e8f5e9',
                                                    color: '#2e7d32',
                                                    borderRadius: '20px',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 500,
                                                    textTransform: 'uppercase'
                                                }}>
                                                    {enrollment.dept}
                                                </span>
                                            </td>
                                            <td style={{ padding: '1rem', color: '#333' }}>
                                                <span style={{
                                                    padding: '0.25rem 0.75rem',
                                                    backgroundColor: '#fff3e0',
                                                    color: '#f57c00',
                                                    borderRadius: '20px',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 500
                                                }}>
                                                    {getYearDisplay(enrollment.year)}
                                                </span>
                                            </td>
                                            <td style={{ padding: '1rem', color: '#333' }}>
                                                <span style={{
                                                    padding: '0.25rem 0.75rem',
                                                    backgroundColor: getStatusColor(enrollment.status).bg,
                                                    color: getStatusColor(enrollment.status).color,
                                                    borderRadius: '20px',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 500,
                                                    textTransform: 'capitalize'
                                                }}>
                                                    {enrollment.status}
                                                </span>
                                            </td>
                                            <td style={{ padding: '1rem', color: '#333' }}>
                                                {enrollment.enrollment_date ? new Date(enrollment.enrollment_date).toLocaleDateString() : 'N/A'}
                                            </td>
                                            <td style={{ padding: '1rem', textAlign: 'center' }}>
                                                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                                                    <button
                                                        onClick={() => handleEditClick(enrollment)}
                                                        style={{
                                                            padding: '0.5rem 1rem',
                                                            backgroundColor: '#e3f2fd',
                                                            color: '#1976d2',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            fontSize: '0.85rem',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.3s ease',
                                                            fontWeight: 500
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.target.style.backgroundColor = '#1976d2';
                                                            e.target.style.color = '#ffffff';
                                                            e.target.style.transform = 'translateY(-1px)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.target.style.backgroundColor = '#e3f2fd';
                                                            e.target.style.color = '#1976d2';
                                                            e.target.style.transform = 'translateY(0)';
                                                        }}
                                                    >
                                                        ✏️ Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteClick(enrollment)}
                                                        style={{
                                                            padding: '0.5rem 1rem',
                                                            backgroundColor: '#ffebee',
                                                            color: '#d32f2f',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            fontSize: '0.85rem',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.3s ease',
                                                            fontWeight: 500
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.target.style.backgroundColor = '#d32f2f';
                                                            e.target.style.color = '#ffffff';
                                                            e.target.style.transform = 'translateY(-1px)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.target.style.backgroundColor = '#ffebee';
                                                            e.target.style.color = '#d32f2f';
                                                            e.target.style.transform = 'translateY(0)';
                                                        }}
                                                    >
                                                        🗑️ Remove
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div style={{
                                padding: '1.5rem',
                                borderTop: '1px solid #f1f5f9',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: '1rem'
                            }}>
                                <div style={{ color: '#666', fontSize: '0.9rem' }}>
                                    Page {currentPage} of {totalPages} • {totalCount} total enrollments (filtered from {allEnrollments.length})
                                </div>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <button
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        style={{
                                            padding: '0.5rem 1rem',
                                            border: '2px solid #e2e8f0',
                                            backgroundColor: currentPage === 1 ? '#f8fafc' : '#ffffff',
                                            color: currentPage === 1 ? '#9ca3af' : '#374151',
                                            borderRadius: '6px',
                                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                            transition: 'all 0.3s ease',
                                            fontSize: '0.9rem'
                                        }}
                                    >
                                        ← Previous
                                    </button>

                                    {[...Array(Math.min(5, totalPages))].map((_, index) => {
                                        let page;
                                        if (totalPages <= 5) {
                                            page = index + 1;
                                        } else if (currentPage <= 3) {
                                            page = index + 1;
                                        } else if (currentPage >= totalPages - 2) {
                                            page = totalPages - 4 + index;
                                        } else {
                                            page = currentPage - 2 + index;
                                        }

                                        return (
                                            <button
                                                key={page}
                                                onClick={() => handlePageChange(page)}
                                                style={{
                                                    padding: '0.5rem 0.75rem',
                                                    border: '2px solid',
                                                    borderColor: page === currentPage ? '#238548' : '#e2e8f0',
                                                    backgroundColor: page === currentPage ? '#238548' : '#ffffff',
                                                    color: page === currentPage ? '#ffffff' : '#374151',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    fontSize: '0.9rem',
                                                    fontWeight: page === currentPage ? '600' : '400'
                                                }}
                                            >
                                                {page}
                                            </button>
                                        );
                                    })}

                                    <button
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        style={{
                                            padding: '0.5rem 1rem',
                                            border: '2px solid #e2e8f0',
                                            backgroundColor: currentPage === totalPages ? '#f8fafc' : '#ffffff',
                                            color: currentPage === totalPages ? '#9ca3af' : '#374151',
                                            borderRadius: '6px',
                                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                            transition: 'all 0.3s ease',
                                            fontSize: '0.9rem'
                                        }}
                                    >
                                        Next →
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>


            {/* Add/Edit Enrollment Modal */}
            {
                (showAddModal || showEditModal) && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000,
                        padding: '1rem'
                    }}>
                        <div style={{
                            background: '#ffffff',
                            borderRadius: '12px',
                            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
                            width: '100%',
                            maxWidth: '600px',
                            maxHeight: '90vh',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            {/* Modal Header */}
                            <div style={{
                                background: 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                                color: '#ffffff',
                                padding: '1.5rem 2rem',
                                borderRadius: '12px 12px 0 0'
                            }}>
                                <h3 style={{
                                    margin: 0,
                                    fontSize: '1.5rem',
                                    fontWeight: 600,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}>
                                    {selectedEnrollment ? '✏️ Edit Training Enrollment' : '➕ Enroll Student in Training'}
                                </h3>
                                <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9, fontSize: '0.95rem' }}>
                                    {selectedEnrollment ? 'Update training enrollment details' : 'Assign student to training program'}
                                </p>
                            </div>

                            {/* Modal Content */}
                            <div style={{
                                padding: '2rem',
                                overflowY: 'auto',
                                flex: 1
                            }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
                                    {/* Student Selection - only for new enrollment */}
                                    {!selectedEnrollment && (
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                fontSize: '0.9rem',
                                                fontWeight: 600,
                                                color: '#374151',
                                                marginBottom: '0.5rem'
                                            }}>
                                                Student ID *
                                            </label>
                                            <input
                                                type="text"
                                                value={formData.student_id}
                                                onChange={(e) => {
                                                    const value = e.target.value.trim();
                                                    setFormData({ ...formData, student_id: value });

                                                    // Clear error when user starts typing
                                                    if (formErrors.student_id) {
                                                        setFormErrors({ ...formErrors, student_id: '' });
                                                    }
                                                }}
                                                placeholder="Enter student ID (e.g., 001-112-2024-0177)"
                                                style={{
                                                    width: '100%',
                                                    padding: '0.75rem',
                                                    border: `2px solid ${formErrors.student_id ? '#dc2626' : '#e5e7eb'}`,
                                                    borderRadius: '8px',
                                                    fontSize: '1rem',
                                                    outline: 'none',
                                                    transition: 'border-color 0.3s ease',
                                                    backgroundColor: formErrors.student_id ? '#fef2f2' : '#ffffff'
                                                }}
                                                onFocus={(e) => {
                                                    if (!formErrors.student_id) e.target.style.borderColor = '#238548';
                                                }}
                                                onBlur={(e) => {
                                                    if (!formErrors.student_id) e.target.style.borderColor = '#e5e7eb';
                                                }}
                                            />
                                            {formErrors.student_id && (
                                                <p style={{
                                                    color: '#dc2626',
                                                    fontSize: '0.8rem',
                                                    margin: '0.25rem 0 0 0',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '0.25rem'
                                                }}>
                                                    ⚠️ {formErrors.student_id}
                                                </p>
                                            )}
                                        </div>
                                    )}

                                    {/* Training Program */}
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.9rem',
                                            fontWeight: 600,
                                            color: '#374151',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Training Program *
                                        </label>
                                        <select
                                            value={formData.training_program_id}
                                            onChange={(e) => {
                                                const programId = e.target.value;
                                                setFormData({ ...formData, training_program_id: programId, training_batch_id: '' });
                                                setTrainingBatches([]);
                                                if (programId) {
                                                    fetchTrainingBatches(programId);
                                                }
                                            }}
                                            disabled={programsLoading}
                                            style={{
                                                width: '100%',
                                                padding: '0.75rem',
                                                border: `2px solid ${formErrors.training_program_id ? '#dc2626' : '#e5e7eb'}`,
                                                borderRadius: '8px',
                                                fontSize: '1rem',
                                                outline: 'none',
                                                transition: 'border-color 0.3s ease',
                                                backgroundColor: formErrors.training_program_id ? '#fef2f2' : '#ffffff',
                                                cursor: programsLoading ? 'not-allowed' : 'pointer'
                                            }}
                                            onFocus={(e) => {
                                                if (!formErrors.training_program_id) e.target.style.borderColor = '#238548';
                                            }}
                                            onBlur={(e) => {
                                                if (!formErrors.training_program_id) e.target.style.borderColor = '#e5e7eb';
                                            }}
                                        >
                                            <option value="">
                                                {programsLoading ? 'Loading programs...' : 'Select Training Program'}
                                            </option>
                                            {trainingPrograms.map(program => (
                                                <option key={program.id} value={program.id}>
                                                    {program.program_name} ({program.program_code})
                                                </option>
                                            ))}
                                        </select>
                                        {formErrors.training_program_id && (
                                            <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                                                {formErrors.training_program_id}
                                            </p>
                                        )}
                                    </div>

                                    {/* Training Batch */}
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.9rem',
                                            fontWeight: 600,
                                            color: '#374151',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Training Batch *
                                        </label>
                                        <select
                                            value={formData.training_batch_id}
                                            onChange={(e) => setFormData({ ...formData, training_batch_id: e.target.value })}
                                            disabled={!formData.training_program_id || trainingBatchesLoading}
                                            style={{
                                                width: '100%',
                                                padding: '0.75rem',
                                                border: `2px solid ${formErrors.training_batch_id ? '#dc2626' : '#e5e7eb'}`,
                                                borderRadius: '8px',
                                                fontSize: '1rem',
                                                outline: 'none',
                                                transition: 'border-color 0.3s ease',
                                                backgroundColor: formErrors.training_batch_id ? '#fef2f2' :
                                                    (!formData.training_program_id) ? '#f9fafb' : '#ffffff',
                                                cursor: (formData.training_program_id && !trainingBatchesLoading) ? 'pointer' : 'not-allowed'
                                            }}
                                            onFocus={(e) => {
                                                if (!formErrors.training_batch_id && formData.training_program_id) e.target.style.borderColor = '#238548';
                                            }}
                                            onBlur={(e) => {
                                                if (!formErrors.training_batch_id) e.target.style.borderColor = '#e5e7eb';
                                            }}
                                        >
                                            <option value="">
                                                {trainingBatchesLoading ? 'Loading batches...' :
                                                    !formData.training_program_id ? 'Select program first' :
                                                        trainingBatches.length === 0 ? 'No batches available' :
                                                            'Select Training Batch'}
                                            </option>
                                            {trainingBatches.map(batch => (
                                                <option key={batch.id} value={batch.id}>
                                                    {batch.batch_name} ({batch.batch_code})
                                                </option>
                                            ))}
                                        </select>
                                        {formErrors.training_batch_id && (
                                            <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                                                {formErrors.training_batch_id}
                                            </p>
                                        )}
                                    </div>

                                    {/* Enrollment Date */}
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.9rem',
                                            fontWeight: 600,
                                            color: '#374151',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Enrollment Date *
                                        </label>
                                        <input
                                            type="date"
                                            value={formData.enrollment_date}
                                            onChange={(e) => setFormData({ ...formData, enrollment_date: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '0.75rem',
                                                border: `2px solid ${formErrors.enrollment_date ? '#dc2626' : '#e5e7eb'}`,
                                                borderRadius: '8px',
                                                fontSize: '1rem',
                                                outline: 'none',
                                                transition: 'border-color 0.3s ease',
                                                backgroundColor: formErrors.enrollment_date ? '#fef2f2' : '#ffffff'
                                            }}
                                            onFocus={(e) => {
                                                if (!formErrors.enrollment_date) e.target.style.borderColor = '#238548';
                                            }}
                                            onBlur={(e) => {
                                                if (!formErrors.enrollment_date) e.target.style.borderColor = '#e5e7eb';
                                            }}
                                        />
                                        {formErrors.enrollment_date && (
                                            <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                                                {formErrors.enrollment_date}
                                            </p>
                                        )}
                                    </div>

                                    {/* Status */}
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.9rem',
                                            fontWeight: 600,
                                            color: '#374151',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Status *
                                        </label>
                                        <select
                                            value={formData.status}
                                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '0.75rem',
                                                border: `2px solid ${formErrors.status ? '#dc2626' : '#e5e7eb'}`,
                                                borderRadius: '8px',
                                                fontSize: '1rem',
                                                outline: 'none',
                                                transition: 'border-color 0.3s ease',
                                                backgroundColor: formErrors.status ? '#fef2f2' : '#ffffff'
                                            }}
                                            onFocus={(e) => {
                                                if (!formErrors.status) e.target.style.borderColor = '#238548';
                                            }}
                                            onBlur={(e) => {
                                                if (!formErrors.status) e.target.style.borderColor = '#e5e7eb';
                                            }}
                                        >
                                            {statusOptions.map(status => (
                                                <option key={status.value} value={status.value}>
                                                    {status.label}
                                                </option>
                                            ))}
                                        </select>
                                        {formErrors.status && (
                                            <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '0.25rem 0 0 0' }}>
                                                {formErrors.status}
                                            </p>
                                        )}
                                    </div>

                                    {/* Remarks */}
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.9rem',
                                            fontWeight: 600,
                                            color: '#374151',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Remarks
                                        </label>
                                        <textarea
                                            value={formData.remarks}
                                            onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                                            placeholder="Optional remarks or notes"
                                            rows={3}
                                            style={{
                                                width: '100%',
                                                padding: '0.75rem',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                fontSize: '1rem',
                                                outline: 'none',
                                                transition: 'border-color 0.3s ease',
                                                backgroundColor: '#ffffff',
                                                resize: 'vertical',
                                                minHeight: '80px'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#238548'}
                                            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div style={{
                                padding: '1.5rem 2rem',
                                borderTop: '1px solid #f1f5f9',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '1rem',
                                backgroundColor: '#f8fafc'
                            }}>
                                <button
                                    onClick={() => {
                                        setShowAddModal(false);
                                        setShowEditModal(false);
                                        resetForm();
                                    }}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        border: '2px solid #e5e7eb',
                                        backgroundColor: '#ffffff',
                                        color: '#374151',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        fontWeight: 500
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.borderColor = '#d1d5db';
                                        e.target.style.backgroundColor = '#f9fafb';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.borderColor = '#e5e7eb';
                                        e.target.style.backgroundColor = '#ffffff';
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={submitting}
                                    style={{
                                        padding: '0.75rem 2rem',
                                        border: 'none',
                                        background: submitting
                                            ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                                            : 'linear-gradient(135deg, #238548 0%, #97d6ac 100%)',
                                        color: '#ffffff',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        cursor: submitting ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.3s ease',
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!submitting) {
                                            e.target.style.transform = 'translateY(-2px)';
                                            e.target.style.boxShadow = '0 8px 25px rgba(35, 133, 72, 0.3)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!submitting) {
                                            e.target.style.transform = 'translateY(0)';
                                            e.target.style.boxShadow = 'none';
                                        }
                                    }}
                                >
                                    {submitting ? (
                                        <>
                                            <div style={{
                                                width: '16px',
                                                height: '16px',
                                                border: '2px solid #ffffff',
                                                borderTop: '2px solid transparent',
                                                borderRadius: '50%',
                                                animation: 'spin 1s linear infinite'
                                            }}></div>
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            {selectedEnrollment ? '💾 Update Enrollment' : '➕ Enroll Student'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Bulk Enrollment Modal */}
            {
                showBulkEnrollModal && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000,
                        padding: '1rem'
                    }}>
                        <div style={{
                            background: '#ffffff',
                            borderRadius: '12px',
                            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
                            width: '100%',
                            maxWidth: '900px',
                            maxHeight: '90vh',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            {/* Modal Header */}
                            <div style={{
                                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                                color: '#ffffff',
                                padding: '1.5rem 2rem',
                                borderRadius: '12px 12px 0 0'
                            }}>
                                <h3 style={{
                                    margin: 0,
                                    fontSize: '1.5rem',
                                    fontWeight: 600,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem'
                                }}>
                                    👥 Bulk Enroll Students in Training
                                </h3>
                                <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9, fontSize: '0.95rem' }}>
                                    Select students by department/year/batch and assign them to a training program
                                </p>
                            </div>

                            {/* Modal Content */}
                            <div style={{
                                padding: '2rem',
                                overflowY: 'auto',
                                flex: 1
                            }}>
                                {/* Step 1: Training Program & Batch Selection */}
                                <div style={{ marginBottom: '2rem' }}>
                                    <h4 style={{
                                        margin: '0 0 1rem 0',
                                        color: '#333',
                                        fontSize: '1.2rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        🎯 Step 1: Select Training Program & Batch
                                    </h4>

                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                                        gap: '1rem',
                                        marginBottom: '1rem'
                                    }}>
                                        {/* Training Program */}
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                fontSize: '0.9rem',
                                                fontWeight: 600,
                                                color: '#374151',
                                                marginBottom: '0.5rem'
                                            }}>
                                                Training Program *
                                            </label>
                                            <select
                                                value={bulkFormData.training_program_id}
                                                onChange={(e) => {
                                                    const programId = e.target.value;
                                                    setBulkFormData({ ...bulkFormData, training_program_id: programId, training_batch_id: '' });
                                                    setTrainingBatches([]);
                                                    if (programId) {
                                                        fetchTrainingBatches(programId);
                                                    }
                                                }}
                                                disabled={programsLoading}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.75rem',
                                                    border: '2px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '1rem',
                                                    outline: 'none',
                                                    transition: 'border-color 0.3s ease',
                                                    backgroundColor: programsLoading ? '#f9fafb' : '#ffffff',
                                                    cursor: programsLoading ? 'not-allowed' : 'pointer'
                                                }}
                                                onFocus={(e) => e.target.style.borderColor = '#1976d2'}
                                                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                            >
                                                <option value="">
                                                    {programsLoading ? 'Loading programs...' : 'Select Training Program'}
                                                </option>
                                                {trainingPrograms.map(program => (
                                                    <option key={program.id} value={program.id}>
                                                        {program.program_name} ({program.program_code})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Training Batch */}
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                fontSize: '0.9rem',
                                                fontWeight: 600,
                                                color: '#374151',
                                                marginBottom: '0.5rem'
                                            }}>
                                                Training Batch *
                                            </label>
                                            <select
                                                value={bulkFormData.training_batch_id}
                                                onChange={(e) => setBulkFormData({ ...bulkFormData, training_batch_id: e.target.value })}
                                                disabled={!bulkFormData.training_program_id || trainingBatchesLoading}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.75rem',
                                                    border: '2px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '1rem',
                                                    outline: 'none',
                                                    transition: 'border-color 0.3s ease',
                                                    backgroundColor: (!bulkFormData.training_program_id) ? '#f9fafb' : '#ffffff',
                                                    cursor: (bulkFormData.training_program_id && !trainingBatchesLoading) ? 'pointer' : 'not-allowed'
                                                }}
                                                onFocus={(e) => {
                                                    if (bulkFormData.training_program_id && !trainingBatchesLoading) {
                                                        e.target.style.borderColor = '#1976d2';
                                                    }
                                                }}
                                                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                            >
                                                <option value="">
                                                    {trainingBatchesLoading ? 'Loading batches...' :
                                                        !bulkFormData.training_program_id ? 'Select program first' :
                                                            trainingBatches.length === 0 ? 'No batches available' :
                                                                'Select Training Batch'}
                                                </option>
                                                {trainingBatches.map(batch => (
                                                    <option key={batch.id} value={batch.id}>
                                                        {batch.batch_name} ({batch.batch_code})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Enrollment Date */}
                                    <div style={{ maxWidth: '300px' }}>
                                        <label style={{
                                            display: 'block',
                                            fontSize: '0.9rem',
                                            fontWeight: 600,
                                            color: '#374151',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Enrollment Date *
                                        </label>
                                        <input
                                            type="date"
                                            value={bulkFormData.enrollment_date}
                                            onChange={(e) => setBulkFormData({ ...bulkFormData, enrollment_date: e.target.value })}
                                            style={{
                                                width: '100%',
                                                padding: '0.75rem',
                                                border: '2px solid #e5e7eb',
                                                borderRadius: '8px',
                                                fontSize: '1rem',
                                                outline: 'none',
                                                transition: 'border-color 0.3s ease',
                                                backgroundColor: '#ffffff'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#1976d2'}
                                            onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                        />
                                    </div>
                                </div>

                                {/* Step 2: Student Selection */}
                                <div>
                                    <h4 style={{
                                        margin: '0 0 1rem 0',
                                        color: '#333',
                                        fontSize: '1.2rem',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}>
                                        👨‍🎓 Step 2: Select Students
                                    </h4>

                                    {/* Student Filters */}
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
                                        gap: '1rem',
                                        marginBottom: '1rem',
                                        padding: '1rem',
                                        backgroundColor: '#f8fafc',
                                        borderRadius: '8px',
                                        border: '1px solid #e2e8f0'
                                    }}>
                                        {/* Department Filter */}
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                fontSize: '0.9rem',
                                                fontWeight: 600,
                                                color: '#374151',
                                                marginBottom: '0.5rem'
                                            }}>
                                                Department *
                                            </label>
                                            <select
                                                value={bulkFormData.student_filter_dept}
                                                onChange={(e) => {
                                                    const dept = e.target.value;
                                                    setBulkFormData({
                                                        ...bulkFormData,
                                                        student_filter_dept: dept,
                                                        student_filter_year: '',
                                                        student_filter_batch: ''
                                                    });
                                                    setBatches([]);
                                                    setAvailableStudents([]);
                                                    setSelectedStudents([]);
                                                    if (dept) {
                                                        fetchAvailableStudents(dept, '', '');
                                                    }
                                                }}
                                                disabled={departmentsLoading}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.75rem',
                                                    border: '2px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '0.9rem',
                                                    outline: 'none',
                                                    transition: 'border-color 0.3s ease',
                                                    backgroundColor: departmentsLoading ? '#f9fafb' : '#ffffff',
                                                    cursor: departmentsLoading ? 'not-allowed' : 'pointer'
                                                }}
                                                onFocus={(e) => e.target.style.borderColor = '#1976d2'}
                                                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                            >
                                                <option value="">
                                                    {departmentsLoading ? 'Loading...' : 'Select Department'}
                                                </option>
                                                {departments.map(dept => (
                                                    <option key={dept.value} value={dept.value}>
                                                        {dept.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Year Filter */}
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                fontSize: '0.9rem',
                                                fontWeight: 600,
                                                color: '#374151',
                                                marginBottom: '0.5rem'
                                            }}>
                                                Year
                                            </label>
                                            <select
                                                value={bulkFormData.student_filter_year}
                                                onChange={(e) => {
                                                    const year = e.target.value;
                                                    setBulkFormData({
                                                        ...bulkFormData,
                                                        student_filter_year: year,
                                                        student_filter_batch: ''
                                                    });
                                                    setBatches([]);
                                                    if (year && bulkFormData.student_filter_dept) {
                                                        const dbYear = 2000 + parseInt(year);
                                                        fetchFilterBatches(bulkFormData.student_filter_dept, dbYear);
                                                        fetchAvailableStudents(bulkFormData.student_filter_dept, year, '');
                                                    } else if (bulkFormData.student_filter_dept) {
                                                        fetchAvailableStudents(bulkFormData.student_filter_dept, '', '');
                                                    }
                                                }}
                                                disabled={!bulkFormData.student_filter_dept}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.75rem',
                                                    border: '2px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '0.9rem',
                                                    outline: 'none',
                                                    transition: 'border-color 0.3s ease',
                                                    backgroundColor: !bulkFormData.student_filter_dept ? '#f9fafb' : '#ffffff',
                                                    cursor: bulkFormData.student_filter_dept ? 'pointer' : 'not-allowed'
                                                }}
                                                onFocus={(e) => {
                                                    if (bulkFormData.student_filter_dept) e.target.style.borderColor = '#1976d2';
                                                }}
                                                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                            >
                                                <option value="">All Years</option>
                                                {yearOptions.map(year => (
                                                    <option key={year.value} value={year.value}>
                                                        {year.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Batch Filter */}
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                fontSize: '0.9rem',
                                                fontWeight: 600,
                                                color: '#374151',
                                                marginBottom: '0.5rem'
                                            }}>
                                                Batch
                                            </label>
                                            <select
                                                value={bulkFormData.student_filter_batch}
                                                onChange={(e) => {
                                                    const batch = e.target.value;
                                                    setBulkFormData({ ...bulkFormData, student_filter_batch: batch });
                                                    if (bulkFormData.student_filter_dept) {
                                                        fetchAvailableStudents(
                                                            bulkFormData.student_filter_dept,
                                                            bulkFormData.student_filter_year,
                                                            batch
                                                        );
                                                    }
                                                }}
                                                disabled={!bulkFormData.student_filter_dept || !bulkFormData.student_filter_year || filterBatchesLoading}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.75rem',
                                                    border: '2px solid #e5e7eb',
                                                    borderRadius: '8px',
                                                    fontSize: '0.9rem',
                                                    outline: 'none',
                                                    transition: 'border-color 0.3s ease',
                                                    backgroundColor: (!bulkFormData.student_filter_dept || !bulkFormData.student_filter_year) ? '#f9fafb' : '#ffffff',
                                                    cursor: (bulkFormData.student_filter_dept && bulkFormData.student_filter_year && !filterBatchesLoading) ? 'pointer' : 'not-allowed'
                                                }}
                                                onFocus={(e) => {
                                                    if (bulkFormData.student_filter_dept && bulkFormData.student_filter_year && !filterBatchesLoading) {
                                                        e.target.style.borderColor = '#1976d2';
                                                    }
                                                }}
                                                onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                                            >
                                                <option value="">
                                                    {filterBatchesLoading ? 'Loading...' :
                                                        !bulkFormData.student_filter_dept || !bulkFormData.student_filter_year ? 'Select dept & year first' :
                                                            'All Batches'}
                                                </option>
                                                {batches.map(batch => (
                                                    <option key={batch} value={batch}>
                                                        {batch}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Students List */}
                                    {studentsLoading ? (
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            padding: '2rem',
                                            flexDirection: 'column',
                                            gap: '1rem'
                                        }}>
                                            <div style={{
                                                width: '30px',
                                                height: '30px',
                                                border: '3px solid #e3f2fd',
                                                borderTop: '3px solid #1976d2',
                                                borderRadius: '50%',
                                                animation: 'spin 1s linear infinite'
                                            }}></div>
                                            <p style={{ color: '#666' }}>Loading students...</p>
                                        </div>
                                    ) : availableStudents.length === 0 ? (
                                        <div style={{
                                            textAlign: 'center',
                                            padding: '2rem',
                                            backgroundColor: '#fef3c7',
                                            borderRadius: '8px',
                                            border: '1px solid #f59e0b'
                                        }}>
                                            <p style={{ margin: 0, color: '#92400e' }}>
                                                {bulkFormData.student_filter_dept
                                                    ? 'No students found with the selected criteria'
                                                    : 'Please select a department to load students'
                                                }
                                            </p>
                                        </div>
                                    ) : (
                                        <div style={{
                                            border: '2px solid #e5e7eb',
                                            borderRadius: '8px',
                                            maxHeight: '300px',
                                            overflow: 'hidden'
                                        }}>
                                            {/* Students Header */}
                                            <div style={{
                                                padding: '1rem',
                                                backgroundColor: '#f8fafc',
                                                borderBottom: '1px solid #e5e7eb',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '1rem'
                                                }}>
                                                    <label style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '0.5rem',
                                                        fontSize: '0.9rem',
                                                        fontWeight: 600,
                                                        color: '#374151',
                                                        cursor: 'pointer'
                                                    }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedStudents.length === availableStudents.length && availableStudents.length > 0}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedStudents(availableStudents.map(s => s.student_id));
                                                                } else {
                                                                    setSelectedStudents([]);
                                                                }
                                                            }}
                                                            style={{ width: '16px', height: '16px' }}
                                                        />
                                                        Select All ({availableStudents.length} students)
                                                    </label>
                                                </div>
                                                <span style={{ fontSize: '0.9rem', color: '#666' }}>
                                                    {selectedStudents.length} selected
                                                </span>
                                            </div>

                                            {/* Students List */}
                                            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                {availableStudents.map((student) => (
                                                    <div
                                                        key={student.student_id}
                                                        style={{
                                                            padding: '0.75rem 1rem',
                                                            borderBottom: '1px solid #f1f5f9',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '1rem',
                                                            backgroundColor: selectedStudents.includes(student.student_id) ? '#f0f9ff' : '#ffffff',
                                                            transition: 'background-color 0.3s ease'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            if (!selectedStudents.includes(student.student_id)) {
                                                                e.currentTarget.style.backgroundColor = '#f8fafc';
                                                            }
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.backgroundColor = selectedStudents.includes(student.student_id) ? '#f0f9ff' : '#ffffff';
                                                        }}
                                                    >
                                                        <label style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '1rem',
                                                            flex: 1,
                                                            cursor: 'pointer'
                                                        }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedStudents.includes(student.student_id)}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setSelectedStudents([...selectedStudents, student.student_id]);
                                                                    } else {
                                                                        setSelectedStudents(selectedStudents.filter(id => id !== student.student_id));
                                                                    }
                                                                }}
                                                                style={{ width: '16px', height: '16px' }}
                                                            />
                                                            <div style={{ flex: 1 }}>
                                                                <div style={{ fontWeight: 500, color: '#333', marginBottom: '0.25rem' }}>
                                                                    {student.name}
                                                                </div>
                                                                <div style={{ fontSize: '0.8rem', color: '#666' }}>
                                                                    {student.student_id} • {student.course_name} • {getYearDisplay(student.year)}
                                                                </div>
                                                            </div>
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div style={{
                                padding: '1.5rem 2rem',
                                borderTop: '1px solid #f1f5f9',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                backgroundColor: '#f8fafc'
                            }}>
                                <div style={{ fontSize: '0.9rem', color: '#666' }}>
                                    {selectedStudents.length} students selected for enrollment
                                </div>

                                <div style={{ display: 'flex', gap: '1rem' }}>
                                    <button
                                        onClick={() => {
                                            setShowBulkEnrollModal(false);
                                            resetBulkForm();
                                        }}
                                        style={{
                                            padding: '0.75rem 1.5rem',
                                            border: '2px solid #e5e7eb',
                                            backgroundColor: '#ffffff',
                                            color: '#374151',
                                            borderRadius: '8px',
                                            fontSize: '1rem',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            fontWeight: 500
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.borderColor = '#d1d5db';
                                            e.target.style.backgroundColor = '#f9fafb';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.borderColor = '#e5e7eb';
                                            e.target.style.backgroundColor = '#ffffff';
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleBulkEnroll}
                                        disabled={submitting || selectedStudents.length === 0 || !bulkFormData.training_program_id || !bulkFormData.training_batch_id}
                                        style={{
                                            padding: '0.75rem 2rem',
                                            border: 'none',
                                            background: (submitting || selectedStudents.length === 0 || !bulkFormData.training_program_id || !bulkFormData.training_batch_id)
                                                ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                                                : 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                                            color: '#ffffff',
                                            borderRadius: '8px',
                                            fontSize: '1rem',
                                            cursor: (submitting || selectedStudents.length === 0 || !bulkFormData.training_program_id || !bulkFormData.training_batch_id) ? 'not-allowed' : 'pointer',
                                            transition: 'all 0.3s ease',
                                            fontWeight: 600,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!submitting && selectedStudents.length > 0 && bulkFormData.training_program_id && bulkFormData.training_batch_id) {
                                                e.target.style.transform = 'translateY(-2px)';
                                                e.target.style.boxShadow = '0 8px 25px rgba(25, 118, 210, 0.3)';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!submitting) {
                                                e.target.style.transform = 'translateY(0)';
                                                e.target.style.boxShadow = 'none';
                                            }
                                        }}
                                    >
                                        {submitting ? (
                                            <>
                                                <div style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    border: '2px solid #ffffff',
                                                    borderTop: '2px solid transparent',
                                                    borderRadius: '50%',
                                                    animation: 'spin 1s linear infinite'
                                                }}></div>
                                                Enrolling...
                                            </>
                                        ) : (
                                            <>
                                                👥 Enroll {selectedStudents.length} Students
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Delete Confirmation Modal */}
            {
                showDeleteModal && selectedEnrollment && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2000,
                        padding: '1rem'
                    }}>
                        <div style={{
                            background: '#ffffff',
                            borderRadius: '12px',
                            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
                            maxWidth: '400px',
                            width: '100%'
                        }}>
                            {/* Modal Header */}
                            <div style={{
                                padding: '2rem 2rem 1rem 2rem',
                                textAlign: 'center'
                            }}>
                                <div style={{
                                    width: '64px',
                                    height: '64px',
                                    background: '#fee2e2',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 1rem',
                                    fontSize: '2rem'
                                }}>
                                    🗑️
                                </div>
                                <h3 style={{
                                    margin: '0 0 1rem 0',
                                    fontSize: '1.5rem',
                                    fontWeight: 600,
                                    color: '#dc2626'
                                }}>
                                    Remove Training Enrollment
                                </h3>
                                <p style={{
                                    margin: '0 0 1rem 0',
                                    color: '#6b7280',
                                    lineHeight: 1.6
                                }}>
                                    Are you sure you want to remove <strong style={{ color: '#374151' }}>{selectedEnrollment.actual_student_name || selectedEnrollment.student_name}</strong> from the training program <strong style={{ color: '#374151' }}>{selectedEnrollment.program_name}</strong>?
                                </p>
                                <p style={{
                                    margin: 0,
                                    color: '#dc2626',
                                    fontSize: '0.9rem',
                                    fontWeight: 500
                                }}>
                                    This action cannot be undone and will permanently remove the enrollment record.
                                </p>
                            </div>

                            {/* Modal Footer */}
                            <div style={{
                                padding: '1rem 2rem 2rem 2rem',
                                display: 'flex',
                                justifyContent: 'center',
                                gap: '1rem'
                            }}>
                                <button
                                    onClick={() => {
                                        setShowDeleteModal(false);
                                        setSelectedEnrollment(null);
                                    }}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        border: '2px solid #e5e7eb',
                                        backgroundColor: '#ffffff',
                                        color: '#374151',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        fontWeight: 500
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.borderColor = '#d1d5db';
                                        e.target.style.backgroundColor = '#f9fafb';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.borderColor = '#e5e7eb';
                                        e.target.style.backgroundColor = '#ffffff';
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDelete}
                                    disabled={submitting}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        border: 'none',
                                        background: submitting
                                            ? 'linear-gradient(135deg, #9ca3af 0%, #d1d5db 100%)'
                                            : 'linear-gradient(135deg, #dc2626 0%, #f87171 100%)',
                                        color: '#ffffff',
                                        borderRadius: '8px',
                                        fontSize: '1rem',
                                        cursor: submitting ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.3s ease',
                                        fontWeight: 600,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!submitting) {
                                            e.target.style.transform = 'translateY(-2px)';
                                            e.target.style.boxShadow = '0 8px 25px rgba(220, 38, 38, 0.3)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!submitting) {
                                            e.target.style.transform = 'translateY(0)';
                                            e.target.style.boxShadow = 'none';
                                        }
                                    }}
                                >
                                    {submitting ? (
                                        <>
                                            <div style={{
                                                width: '16px',
                                                height: '16px',
                                                border: '2px solid #ffffff',
                                                borderTop: '2px solid transparent',
                                                borderRadius: '50%',
                                                animation: 'spin 1s linear infinite'
                                            }}></div>
                                            Removing...
                                        </>
                                    ) : (
                                        '🗑️ Remove Enrollment'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </Layout>
    );
};

export default AdminTrainingManagement;